import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import {useRef, useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {cnb} from 'cnbuilder';
import {v4 as uuidv4} from 'uuid';
import add from 'date-fns/add';
import addHours from 'date-fns/addHours';
import differenceInYears from 'date-fns/differenceInYears';
import endOfMonth from 'date-fns/endOfMonth';
import parse from 'date-fns/parse';
import startOfMonth from 'date-fns/startOfMonth';
import {format} from 'date-fns-tz';
import {useI18nContext} from '@teladoc/pulse/ui/Context/i18n';
import Button from '@teladoc/pulse/ui/Button';
import DatePicker from '@teladoc/pulse/ui/DatePicker';
import Form from '@teladoc/pulse/ui/Form';
import IconInfoDefault from '@teladoc/pulse/icons/info-default.svg';
import FormElementError from '@teladoc/pulse/ui/FormElementError';
import Label from '@teladoc/pulse/ui/Label';
import Select from '@teladoc/pulse/ui/Select';
import Chip from '@teladoc/pulse/ui/Chip';
import CheckableGroup from '@teladoc/pulse/ui/CheckableGroup';
import RadioGroup from '@teladoc/pulse/ui/RadioGroup';
import TextArea from '@teladoc/pulse/ui/TextArea';
import Modal from '@teladoc/pulse/ui/Modal';
import {useFeatureFlagContext} from '@livongo/utilities/system/featureFlag';
import Arg from '@livongo/arg';
import useTransLoader from 'i18n/use-trans-loader';
import AcuityError from '../ErrorMessages/AcuityError';
import CommonUtils from '../utils/common-utils';
import UserInfo from '../UserInfo/UserInfo';
import SharedCoaching from '../utils/SharedCoaching';
import UserInfoAPI from '../UserInfo/userInfo-api';
import TimeTap from '../utils/TimeTap';
import {
    timeZones,
    defaultTimeZone,
    localizedTime,
    getClientTimeZoneObject,
} from '../utils/Timezone';
import {
    PRODUCTION_SERVICES_IDS,
    TESTING_SERVICES_IDS,
    ANY_COACH,
    IS_PROD,
    FEATURE_FLAG_NAMES,
    ENABLE_TRACKING_APPOINTMENT_REQUEST,
} from '../config';
import MixpanelUtils from '../common/mix-panel';
import AmpliUtils from '../utils/amplitude-utils';
import {TRACK_EVENT_KEYS} from '../constants/amplitude';
import Loader from '../common/loader/Loader';
import css2 from './SchedulingFormV2.scss';

const MONTHS_AHEAD = 2;
const CONFIRMATION = [
    {
        key: 'No',
        value: true,
    },
    {
        key: 'Yes',
        value: false,
    },
];
const DATE_FORMAT = 'yyyy-MM-dd';
const SESSION_DURATION = '30';
const languages = [
    {
        key: 'english',
        value: 'en-US',
    },
    {
        key: 'spanish',
        value: 'es-US',
    },
];
const coaches = [
    {
        key: '',
        value: '',
    },
    {
        key: ANY_COACH,
        value: 'Different coach',
    },
];
const WM_PROGRAM = 'WEIGHT_MANAGEMENT';
const AWM_PROGRAM = 'ADV_WEIGHT_MANAGEMENT';
const ADPP_PROGRAM = 'ADV_PREDIABETES';
const CWC_PROGRAM = 'COMPR_WEIGHT_CARE';
const CDPC_PROGRAM = 'COMPR_PREDIABETES_CARE';
const ADOL_WM_PROGRAM = 'ADOLESCENT_WEIGHT_MANAGEMENT';
const RANKED_PROGRAMS = [
    ADOL_WM_PROGRAM,
    'HEART_FAILURE',
    'DIABETES',
    'DIABETES_DEVICEFLEX',
    'PREDIABETES',
    ADPP_PROGRAM,
    CDPC_PROGRAM,
    'HYPERTENSION',
    'CHRONIC_KIDNEY_DISEASE',
    WM_PROGRAM,
    AWM_PROGRAM,
    CWC_PROGRAM,
];
const ADOLESCENT_AGE_LIMIT = 17;
const webSessionId = crypto.randomUUID();

const SchedulingFormV2 = () => {
    const {t} = useTransLoader('schedulingForm');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const contentRef = useRef();
    const lastCallTimeRef = useRef(0); // Track last call time for debouncing
    const DEBOUNCE_DELAY_MS = 1000; // Minimum time between API calls to prevent rapid successive calls
    const {selectedCountry, selectedLang, changeLanguage} = useI18nContext();
    const {
        appointmentTypesInfo,
        firstName,
        lastName,
        email,
        phones,
        birthDate,
        pid,
    } = useSelector(state => state.userData.userData);
    const [hasSessionInPastYear, setHasSessionInPastYear] = useState(null);
    const {token, uuid} = useSelector(state => state.userData.auth);
    const [selectedTimezone, setSelectedTimezone] = useState(
        defaultTimeZone(selectedCountry, selectedLang)
    );
    const [APIError, setAPIError] = useState();
    const [sessionToken, setSessionToken] = useState(token);
    const [rankedProgram, setRankedProgram] = useState(null);
    const [serviceId, setServiceId] = useState(null);
    const [hasPastSession, setHasPastSession] = useState(false);
    const [selectedAnyCoach, setSelectedAnyCoach] = useState(false);
    const [availableDatesData, setAvailableDatesData] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [initialMonth, setInitialMonth] = useState();
    const [filteredTimeSlots, setFilteredTimeSlots] = useState(null);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [timeSlotsData, setTimeSlotsData] = useState(null);
    const [selectedTimeSlotData, setSelectedTimeSlotData] = useState(null);
    const [clientObject, setClientObject] = useState(null);
    const [locationObject, setLocationObject] = useState(null);
    const [reasonObject, setReasonObject] = useState(null);
    const [commentFieldObject, setCommentFieldObject] = useState(null);
    const [coachesData, setCoachesData] = useState(null);
    const [listOfCoachNames, setListOfCoachNames] = useState([]);
    const [selectedCoachId, setSelectedCoachId] = useState(null);
    const [disableScheduleButton, setDisableScheduleButton] = useState(false);
    const [coachId, setCoachId] = useState(null);
    const [notes, setNotes] = useState('');
    const [timeKey, setTimeKey] = useState(uuidv4());
    const [sessionKey, setSessionKey] = useState('0');
    const [uuidFieldObject, setUuidFieldObject] = useState(null);
    const [sourceFieldObject, setSourceFieldObject] = useState(null);
    const [internalUserId, setInternalUserId] = useState(null);
    const [showFirstVisitMsg, setShowFirstVisitMsg] = useState(false);
    const [showPastVisitMsg, setShowPastVisitMsg] = useState(false);
    const [previousSessionCoach, setPreviousSessionCoach] = useState(null);
    const [previousSessionReason, setPreviousSessionReason] = useState(null);
    const [coachesInServiceId, setCoachesInServiceId] = useState([]);
    const [isAlreadyBooked, setIsAlreadyBooked] = useState(null);
    const [showWaitModal, setShowWaitModal] = useState(true);
    const [
        prevCoachHasNoAvailable2MonthsOut,
        setPreviousCoachNotAvailable2MonthsOut,
    ] = useState(false);
    const showPastSession = false;
    const oneApp =
        Arg('oneapp') ||
        Arg('ccmOneApp') ||
        window.location.hostname.includes('teladoc');
    const serviceTypesIds = IS_PROD
        ? PRODUCTION_SERVICES_IDS
        : TESTING_SERVICES_IDS;
    const TIMETAP_CCM_LOCATION_ID =
        window?.process?.env?.TIMETAP_CCM_LOCATION_ID ??
        process.env.TIMETAP_CCM_LOCATION_ID;
    // eslint-disable-next-line no-unused-vars
    const [enableNewUi, setEnableNewUi] = useState(false); // TODO: Remove this feature flag
    const {featureFlags} = useFeatureFlagContext();
    const isMobile = Arg('mobile') === true;
    const [isPreviousCoachSelected, setIsPreviousCoachSelected] =
        useState(true);
    // Use a ref to track if getLastSessionData & getLocationData have been called to prevent multiple calls
    const hasCalledLastSessionData = useRef(false);
    const hasCalledGetLocationData = useRef(false);
    const timezoneChange = timezone => {
        const timeZoneValues = getClientTimeZoneObject(
            selectedCountry,
            timezone.value
        );

        setAvailableTimes(
            availableTimes.map(timeSlot => {
                const formattedTime = new Date(
                    Number(timeSlot?.value)
                ).toISOString();

                return {
                    label: localizedTime(
                        formattedTime,
                        timezone.value,
                        selectedLang
                    ),
                    value: timeSlot?.value,
                };
            })
        );
        setSelectedTimezone(timezone);

        // Track timezone change
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.TIMEZONE_CHANGED, {
            [TRACK_EVENT_KEYS.SelectedTimeZone]: timezone?.value,
        });

        clientObject.timeZone.timeZoneCode = timeZoneValues?.timeZoneCode;
        clientObject.timeZone.timeZoneDesc = timeZoneValues?.timeZoneDesc;
        clientObject.timeZone.timeZoneId = timeZoneValues?.timeZoneId;

        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.timezone',
        });
    };

    const setServiceTypeId = useCallback(() => {
        const programs = [];
        const age = differenceInYears(new Date(), new Date(birthDate));

        if (appointmentTypesInfo) {
            appointmentTypesInfo.forEach(type => {
                switch (type.program) {
                    case ADPP_PROGRAM:
                        programs.push(ADPP_PROGRAM);
                        break;
                    case AWM_PROGRAM:
                        programs.push(AWM_PROGRAM);
                        break;
                    case CDPC_PROGRAM:
                        programs.push(CDPC_PROGRAM);
                        break;
                    case CWC_PROGRAM:
                        programs.push(CWC_PROGRAM);
                        break;
                    case WM_PROGRAM:
                        programs.push(
                            age <= ADOLESCENT_AGE_LIMIT
                                ? ADOL_WM_PROGRAM
                                : WM_PROGRAM
                        );
                        break;
                    default:
                        programs.push(type.program);
                        break;
                }
            });
        }

        // https://teladocpa.sharepoint.com/:x:/s/CoachingTeamLibrary/Eb3t_Q5mDTdImG_2x3SzICIBtnU6l_ISELYoEquncE5r8w?e=m7iekB
        // i.e. If a member is enrolled in both DM and WM, they will be assigned a DM coach
        const topProgram = RANKED_PROGRAMS.find(program =>
            programs.includes(program)
        );

        setHasPastSession(hasSessionInPastYear);
        setShowFirstVisitMsg(hasSessionInPastYear === false);
        setShowPastVisitMsg(hasSessionInPastYear === true);
        setRankedProgram(topProgram);
        setServiceId(
            serviceTypesIds[
                `${topProgram}${
                    !hasSessionInPastYear ? '_INITIAL_' : '_FOLLOWUP_'
                }${selectedLang === 'en-US' ? 'ENGLISH' : 'SPANISH'}`
            ]
        );
    }, [
        appointmentTypesInfo,
        hasSessionInPastYear,
        serviceTypesIds,
        selectedLang,
        birthDate,
    ]);

    const createNewClientObject = async () => {
        const timeZoneValues = getClientTimeZoneObject(
            selectedCountry,
            selectedTimezone.value
        );
        const clientData = {
            allowWaitListText: false,
            cellPhone: phones[0]?.number,
            emailAddress: email,
            firstName,
            fullName: `${firstName} ${lastName}`,
            lastName,
            locale: selectedLang,
            status: 'ACTIVE',
            timeZone: {
                timeZoneCode: timeZoneValues.timeZoneCode,
                timeZoneDesc: timeZoneValues.timeZoneDesc,
                timeZoneId: timeZoneValues.timeZoneId,
                visible: true,
            },
        };

        try {
            const data = await TimeTap.createNewClientObject(
                sessionToken,
                clientData
            );

            setClientObject(data);
        } catch (error) {
            setAPIError(error);
        }

        return;
    };

    const getClientObject = useCallback(() => {
        TimeTap.getClientObject(sessionToken, email)
            .then(data => {
                // Member is already in TimeTap database
                if (data?.length > 1) {
                    setClientObject(data[0]);
                }
                // Member needs to be added in the TimeTap database
                else if (data?.length === 1 && data[0]?.clientId === -1) {
                    createNewClientObject();
                }
            })
            .catch(error => {
                setAPIError(error);
            });

        return;
    }, [sessionToken, email]); // eslint-disable-line react-hooks/exhaustive-deps

    const getLocationObject = useCallback(() => {
        // Prevents multiple calls. Only needs to be called once.
        if (!hasCalledGetLocationData.current) {
            TimeTap.getLocationObject(sessionToken, TIMETAP_CCM_LOCATION_ID)
                .then(data => {
                    setLocationObject(data);
                    hasCalledGetLocationData.current = true;
                })
                .catch(error => {
                    setAPIError(error);
                });
        }
    }, [sessionToken, TIMETAP_CCM_LOCATION_ID]);

    const getReasonObject = useCallback(() => {
        TimeTap.getReasonObject(sessionToken, serviceId)
            .then(data => {
                setReasonObject(data);
            })
            .catch(error => {
                setAPIError(error);
            });

        return;
    }, [sessionToken, serviceId]);

    const getCoaches = useCallback(() => {
        TimeTap.getStaff(sessionToken, serviceId, TIMETAP_CCM_LOCATION_ID).then(
            data => {
                // 1) getCoaches gets called multiple times because of serviceId changes
                // 2) this is to keep track coaches that are associated with serviceIds already called
                coachesInServiceId.push({
                    serviceId,
                    coaches: data,
                });
                setCoachesInServiceId(coachesInServiceId);
                setCoachesData(data);

                const coachNames = [];

                if (coaches[0].value !== '') {
                    coachNames.push({
                        label: coaches[0].value,
                        value: coaches[0].key,
                    });
                }

                coachNames.push({
                    label: t('sessionInfo.anyCoach'),
                    value: ANY_COACH,
                });

                const dataArray = [];

                data?.forEach(coach => {
                    coachNames.push({
                        label: coach.internalDisplayName,
                        value: coach.professionalId.toString(),
                    });
                    dataArray.push({
                        label: coach.internalDisplayName,
                        value: coach.professionalId.toString(),
                        timeZone: coach.timeZone,
                    });
                });
                setListOfCoachNames(coachNames);

                if (ENABLE_TRACKING_APPOINTMENT_REQUEST) {
                    MixpanelUtils.track({
                        event: 'coaching.scheduling.getCoaches',
                        properties: {
                            webSessionId,
                            coachNames: dataArray,
                            serviceId,
                        },
                    });
                }
            }
        );

        return;
    }, [
        sessionToken,
        serviceId,
        TIMETAP_CCM_LOCATION_ID,
        t,
        coachesInServiceId,
    ]);

    const getAllCoachesAvailableDates = useCallback(async () => {
        // Prevent multiple calls with a simple debounce using timestamp
        const now = Date.now();

        if (
            lastCallTimeRef.current &&
            now - lastCallTimeRef.current < DEBOUNCE_DELAY_MS
        ) {
            return;
        }
        lastCallTimeRef.current = now;

        const dateInstance = new Date();
        const data = [];

        // Helper function to process available dates data
        const processAvailableDates = responseData => {
            const availableDatesResponse = responseData
                ?.flat()
                ?.filter(availableDate => availableDate.timeSlots.length);

            const parsedDates = availableDatesResponse?.map(({date}) =>
                parse(date, DATE_FORMAT, new Date())
            );

            const formattedDates = parsedDates?.map(date =>
                format(date, 'MM/dd/yyyy')
            );

            // Filter time slots for current day (2 hours in advance)
            if (format(dateInstance, 'MM/dd/yyyy') === formattedDates[0]) {
                const twoHoursFromNow = addHours(new Date(), 2).getTime();
                const filteredTimes =
                    availableDatesResponse[0]?.timeSlots.filter(
                        timeSlot =>
                            timeSlot?.staffStartDateTimeUTC >= twoHoursFromNow
                    );

                if (isEmpty(filteredTimes)) {
                    // Remove current day if no available times 2+ hours in advance
                    formattedDates.shift();
                } else {
                    setFilteredTimeSlots(filteredTimes);
                }
            }

            return {availableDatesResponse, parsedDates, formattedDates};
        };

        // Helper function to update state with processed data
        const updateState = (processedData, i) => {
            const {availableDatesResponse, parsedDates, formattedDates} =
                processedData;

            setAvailableDatesData(availableDatesResponse);
            setAvailableDates(formattedDates);

            if (formattedDates?.length && (i === 0 || i === MONTHS_AHEAD)) {
                setInitialMonth(format(parsedDates[0], 'MM/dd/yyyy'));
                setShowWaitModal(false);
            } else if (!formattedDates?.length && i === MONTHS_AHEAD) {
                setShowWaitModal(false);
                setAPIError({});
            }
        };

        // Helper function to generate date range for a given month offset
        const getDateRange = monthOffset => {
            const baseDate = new Date();

            if (monthOffset === 0) {
                return {
                    startDate: format(baseDate, DATE_FORMAT),
                    endDate: format(endOfMonth(baseDate), DATE_FORMAT),
                };
            }

            const targetMonth = add(baseDate, {months: monthOffset});

            return {
                startDate: format(startOfMonth(targetMonth), DATE_FORMAT),
                endDate: format(endOfMonth(targetMonth), DATE_FORMAT),
            };
        };

        try {
            for (let i = 0; i <= MONTHS_AHEAD; i++) {
                const {startDate, endDate} = getDateRange(i);

                // Make API request serially
                // eslint-disable-next-line no-await-in-loop
                const response = await TimeTap.getAllCoachesAvailableDates(
                    sessionToken,
                    serviceId,
                    startDate,
                    endDate,
                    TIMETAP_CCM_LOCATION_ID
                );

                if (response) {
                    data.push(response);
                }

                // Process and update state after first and last API call
                if (i === 0 || i === MONTHS_AHEAD) {
                    const processedData = processAvailableDates(data);

                    updateState(processedData, i);
                }
            }
        } catch (error) {
            setAPIError(error);
        }
    }, [serviceId, sessionToken, TIMETAP_CCM_LOCATION_ID]);

    // Trigerring a re-render for the time selection so that it shows the placeholder text for the new day selected
    const renderTimeSelection = () => {
        setTimeKey(uuidv4());
        setAvailableTimes([]);
    };

    const getCoachAvailableDates = useCallback(() => {
        if (
            previousSessionReason &&
            previousSessionCoach &&
            coachesInServiceId?.length > 0
        ) {
            let coachFoundInPreviousService;
            const serviceMatch = coachesInServiceId?.find(
                service =>
                    service.serviceId?.toString() ===
                    previousSessionReason?.toString()
            );

            if (serviceMatch?.coaches?.length > 0) {
                coachFoundInPreviousService = serviceMatch.coaches.find(
                    coach =>
                        coach.professionalId?.toString() ===
                        previousSessionCoach?.toString()
                );
            } else {
                coachFoundInPreviousService = null;
            }

            if (!coachFoundInPreviousService) {
                setSelectedAnyCoach(true);
                setSelectedCoachId(ANY_COACH);
                setPreviousCoachNotAvailable2MonthsOut(true);
                getAllCoachesAvailableDates();

                return;
            }
        }

        const dateInstance = new Date();
        const requests = [];
        const monthsAhead = 2;

        for (let i = 0; i <= monthsAhead; i++) {
            const year = format(add(dateInstance, {months: i}), 'yyyy');
            const month = format(add(dateInstance, {months: i}), 'MM');

            if (selectedCoachId !== ANY_COACH) {
                requests.push(
                    TimeTap.getCoachAvailableDates(
                        sessionToken,
                        year,
                        month,
                        selectedCoachId ?? coaches[0].key,
                        serviceId,
                        SESSION_DURATION,
                        TIMETAP_CCM_LOCATION_ID
                    )
                );
            }
        }

        if (!requests?.length) {
            setShowWaitModal(false);

            return;
        }

        Promise.all(requests)
            .then(data => {
                const availableDatesResponse = data?.flat();
                const formattedDates = availableDatesResponse.map(
                    ({month, day, year}) => {
                        const date = month
                            .toString()
                            .concat(
                                '/',
                                day.toString().concat('/', year.toString())
                            );
                        const parsedDate = parse(
                            date,
                            'M/d/yyyy',
                            dateInstance
                        );

                        return format(parsedDate, 'MM/dd/yyyy');
                    }
                );

                // Getting time slots for current day to see if there are any available that
                // are 2 hours in advance in order to mark this day in the calendar as available
                if (format(new Date(), 'MM/dd/yyyy') === formattedDates[0]) {
                    TimeTap.getCoachAvailableTimes(
                        sessionToken,
                        dateInstance.getFullYear(),
                        format(dateInstance, 'MM'),
                        format(dateInstance, 'dd'),
                        selectedCoachId ?? coaches[0].key,
                        serviceId,
                        SESSION_DURATION,
                        TIMETAP_CCM_LOCATION_ID
                    ).then(timesData => {
                        if (!isEmpty(timesData)) {
                            const startTime = addHours(new Date(), 2).getTime();
                            const filteredTimes = timesData.filter(
                                // eslint-disable-next-line max-nested-callbacks
                                timeSlot =>
                                    timeSlot?.staffStartDateTimeUTC >= startTime
                            );

                            if (isEmpty(filteredTimes)) {
                                // Remove current day because there's no available times that are 2 hours in advance
                                formattedDates.shift();
                            } else {
                                setFilteredTimeSlots(filteredTimes);
                            }
                        }
                    });
                }
                setAvailableDates(formattedDates);

                if (formattedDates.length) {
                    setInitialMonth(formattedDates[0]);
                    setShowWaitModal(false);
                    setPreviousCoachNotAvailable2MonthsOut(false);
                }

                if (formattedDates?.length === 0) {
                    // add a slight delay to allow the state change to execute properly
                    setInitialMonth(null);
                    setAvailableDates([]);
                    setSelectedAnyCoach(true);
                    setSelectedCoachId(ANY_COACH);
                    setPreviousCoachNotAvailable2MonthsOut(true);
                    getAllCoachesAvailableDates();
                }
            })
            .catch(error => {
                setAPIError(error);
            });

        return;
    }, [
        sessionToken,
        selectedCoachId,
        serviceId,
        TIMETAP_CCM_LOCATION_ID,
        coachesInServiceId,
        getAllCoachesAvailableDates,
        previousSessionCoach,
        previousSessionReason,
    ]);

    const getLastSessionData = useCallback(() => {
        if (!isPreviousCoachSelected) {
            return;
        } else {
            TimeTap.getLastSessionData(
                sessionToken,
                clientObject.clientId
            ).then(data => {
                const professionalId = data?.staff?.professionalId?.toString();

                if (professionalId) {
                    setPreviousCoachNotAvailable2MonthsOut(false);
                    setPreviousSessionReason(data?.reason?.reasonId);
                    setHasSessionInPastYear(true);
                    setPreviousSessionCoach(professionalId);
                    coaches[0].key = professionalId;
                    coaches[0].value = data?.staff?.fullName;
                    setSelectedCoachId(professionalId);
                    setSelectedAnyCoach(false);
                    getCoachAvailableDates();
                } else {
                    setHasSessionInPastYear(false);
                }

                if (
                    listOfCoachNames.find(
                        ({value}) =>
                            value?.toString() === professionalId?.toString()
                    )
                ) {
                    setSelectedCoachId(professionalId);
                }
                // There is an edge case where the coach from the last session isn't in
                // the list of coach names because i.e. they don't have availability in the
                // next 3 months, so we pre-select the "Any Coach" option in the dropdown
                else {
                    setSelectedAnyCoach(true);
                    setSelectedCoachId(ANY_COACH);
                    getAllCoachesAvailableDates();
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sessionToken,
        clientObject,
        listOfCoachNames,
        isPreviousCoachSelected,
        // Removed getAllCoachesAvailableDates and getCoachAvailableDates to break circular dependency
    ]);

    const onDaySelected = date => {
        const dateInstance = new Date(date);

        if (coachId) {
            setCoachId(null);
        }

        if (availableTimes) {
            renderTimeSelection();
        }

        // Track date selection
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.DATE_SELECTED, {
            [TRACK_EVENT_KEYS.SelectedDate]: date,
        });

        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.date',
        });

        if (selectedAnyCoach) {
            let timeSlots;

            // For the current day, we already filtered the time slots in getAllCoachesAvailableDates()
            if (dateInstance.toDateString() === new Date().toDateString()) {
                const dateObjectTemp = {
                    timeSlots: filteredTimeSlots,
                };
                const tempDateData = availableDatesData
                    ? TimeTap.getOtherCoachesAvailableDatesMinusTarget(
                          [dateObjectTemp],
                          previousSessionCoach
                      )
                    : [dateObjectTemp];

                timeSlots = availableDatesData
                    ? tempDateData[0]?.tsTemp
                    : filteredTimeSlots;
            } else {
                const formattedDate = format(dateInstance, DATE_FORMAT);
                const tempDateData = previousSessionCoach
                    ? TimeTap.getOtherCoachesAvailableDatesMinusTarget(
                          availableDatesData,
                          previousSessionCoach
                      )
                    : availableDatesData;
                const dateData = tempDateData.filter(
                    availableDate => availableDate.date === formattedDate
                );

                // const dateData = availableDatesData.filter(
                //     availableDate => availableDate.date === formattedDate
                // );

                timeSlots =
                    dateData[0]?.tsTemp?.length > 0
                        ? dateData[0]?.tsTemp
                        : dateData[0].timeSlots;
            }

            setTimeSlotsData(timeSlots);
            setAvailableTimes(
                timeSlots.map(timeSlot => {
                    const convertedDate = new Date(
                        timeSlot?.staffStartDateTimeUTC
                    ).toISOString();

                    return {
                        label: localizedTime(
                            convertedDate,
                            selectedTimezone.value,
                            selectedLang
                        ),
                        value: timeSlot?.staffStartDateTimeUTC.toString(),
                    };
                })
            );
        } else {
            const splitDate = date.split('/');
            const month = splitDate[0];
            const day = splitDate[1];
            const year = splitDate[2];

            TimeTap.getCoachAvailableTimes(
                sessionToken,
                year,
                month,
                day,
                selectedCoachId,
                serviceId,
                SESSION_DURATION,
                TIMETAP_CCM_LOCATION_ID
            )
                .then(data => {
                    let timeSlots;

                    // For the current day, we already filtered the time slots in getCoachAvailableDates()
                    if (
                        dateInstance.toDateString() ===
                        new Date().toDateString()
                    ) {
                        timeSlots = filteredTimeSlots;
                    } else {
                        timeSlots = data;
                    }

                    setTimeSlotsData(timeSlots);
                    setAvailableTimes(
                        timeSlots?.map(timeSlot => {
                            const convertedDate = new Date(
                                timeSlot.staffStartDateTimeUTC
                            ).toISOString();

                            return {
                                label: localizedTime(
                                    convertedDate,
                                    selectedTimezone.value,
                                    selectedLang
                                ),
                                value: timeSlot?.staffStartDateTimeUTC.toString(),
                            };
                        })
                    );
                })
                .catch(error => {
                    setAPIError(error);
                });
        }
    };

    const onTimeSelected = time => {
        const timeSlotData = timeSlotsData.filter(
            timeSlot => timeSlot?.staffStartDateTimeUTC === Number(time.value)
        );

        setSelectedTimeSlotData(timeSlotData[0]);

        // Track time selection
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.TIME_SELECTED, {
            [TRACK_EVENT_KEYS.SelectedTime]: time?.label,
            [TRACK_EVENT_KEYS.TimeValue]: time?.value,
        });

        if (
            selectedAnyCoach ||
            showFirstVisitMsg ||
            coaches[0].key === '' ||
            coaches[0].value === ''
        ) {
            if (coachId) {
                setCoachId(null);
            }

            const timeSlotInfo = timeSlotData[0].units;

            // Tracking event for Mixpanel
            MixpanelUtils.track({
                event: 'coaching.clicked.ontime',
                properties: {
                    webSessionId,
                    timeSlotInfo,
                    'initial Session': showFirstVisitMsg,
                    selectedTime: time,
                },
            });

            // More than 1 coach has the time slot available
            if (timeSlotInfo.length > 1) {
                const requests = [];

                timeSlotInfo.forEach(timeSlot => {
                    if (
                        timeSlot?.professionalId?.toString() !==
                        previousSessionCoach?.toString()
                    ) {
                        requests.push(
                            TimeTap.getCoachAppointmentCount(
                                sessionToken,
                                timeSlot?.professionalId
                            )
                        );
                    }
                });
                Promise.all(requests).then(data => {
                    // Tracking event for number of coaches available for the selected Time
                    if (ENABLE_TRACKING_APPOINTMENT_REQUEST) {
                        MixpanelUtils.track({
                            event: 'coaching.scheduling.checking.coaches.appointment.count',
                            properties: {
                                webSessionId,
                                coachesAppointmentCount: data,
                            },
                        });
                    }
                    // Finding which coach has the least amount of appointments
                    const coachInfo = data.reduce((prev, curr) => {
                        if (prev.count === curr.count) {
                            return 'sameCount';
                        }

                        return prev.count < curr.count ? prev : curr;
                    });

                    if (coachInfo === 'sameCount') {
                        // Picking a coach randomly
                        setCoachId(
                            timeSlotInfo[
                                Math.floor(Math.random() * timeSlotInfo.length)
                            ].professionalId
                        );
                    } else {
                        setCoachId(coachInfo.coachId);
                    }
                });
            } else {
                setCoachId(timeSlotInfo[0].professionalId);
            }
        } else {
            setCoachId(selectedCoachId ?? coaches[0].key);
            MixpanelUtils.track({
                event: 'coaching.clicked.ontime',
                properties: {
                    selectedCoachId,
                    'initial Session': showFirstVisitMsg,
                },
            });
        }
    };

    const scheduleAppointment = async () => {
        setDisableScheduleButton(true);
        setSessionKey(uuidv4());

        // Track schedule button click
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.SCHEDULE_BUTTON_CLICKED, {
            [TRACK_EVENT_KEYS.HasNotes]: notes?.trim()?.length > 0,
            [TRACK_EVENT_KEYS.IsSharedCoaching]: false,
        });

        const coachData = coachesData.filter(
            coach =>
                coach.professionalId?.toString() === coachId?.toString() ||
                coach.professionalId?.toString() === selectedCoachId?.toString()
        );
        const timeZoneValues = getClientTimeZoneObject(
            selectedCountry,
            selectedTimezone.value
        );

        clientObject.timeZone.timeZoneCode = timeZoneValues?.timeZoneCode;
        clientObject.timeZone.timeZoneDesc = timeZoneValues?.timeZoneDesc;
        clientObject.timeZone.timeZoneId = timeZoneValues?.timeZoneId;

        if (uuidFieldObject) {
            uuidFieldObject.value = internalUserId;
        }

        // Set source information for tracking
        if (sourceFieldObject) {
            sourceFieldObject.value = 'CCM Web Coaching';
        }

        if (commentFieldObject) {
            commentFieldObject.value = notes;
        }

        const appointmentData = {
            client: clientObject,
            clientEndDate: selectedTimeSlotData.clientEndDate,
            clientEndTime: selectedTimeSlotData.clientEndTime,
            clientReminderHours: 24,
            clientStartDate: selectedTimeSlotData.clientStartDate,
            clientStartTime: selectedTimeSlotData.clientStartTime,
            endDate: selectedTimeSlotData.staffEndDate,
            endTime: selectedTimeSlotData.staffEndTime,
            fields: [
                ...(commentFieldObject ? [commentFieldObject] : []),
                ...(uuidFieldObject ? [uuidFieldObject] : []),
                ...(sourceFieldObject ? [sourceFieldObject] : []),
            ],
            location: locationObject,
            reason: reasonObject,
            remindStaffSmsHrs: 0,
            remindClientSmsHrs: 24,
            sendConfirmationToClient: true,
            sendConfirmationToStaff: true,
            staff: coachData[0],
            staffReminderHours: 24,
            startDate: selectedTimeSlotData.staffStartDate,
            startTime: selectedTimeSlotData.staffStartTime,
            status: 'OPEN',
        };

        if (ENABLE_TRACKING_APPOINTMENT_REQUEST) {
            MixpanelUtils.track({
                event: 'coaching.scheduling.appointment.request',
                properties: {
                    webSessionId,
                    schedulingForm: 'CCM-V2',
                    reason: {
                        internalDisplayName:
                            appointmentData?.reason?.internalDisplayName,
                        reasonId: appointmentData?.reason?.reasonId,
                    },
                    staff: {
                        name: appointmentData?.staff?.internalDisplayName,
                        email: appointmentData?.staff?.email,
                    },
                    clientDateTime: {
                        clientStartDate: selectedTimeSlotData.clientStartDate,
                        clientStartTime: selectedTimeSlotData.clientStartTime,
                        clientEndTime: selectedTimeSlotData.clientEndTime,
                    },
                },
            });
        }

        const status = await TimeTap.checkCoachWorkingStatusForDateAndTime(
            sessionToken,
            coachData[0]?.professionalId || coachData[0]?.value,
            selectedTimeSlotData.staffStartDate,
            selectedTimeSlotData.staffStartTime,
            serviceId,
            TIMETAP_CCM_LOCATION_ID
        ).catch(() => {
            return null;
        });

        if (!status?.isWorking) {
            setDisableScheduleButton(false);
            setAPIError({
                data: {
                    details: t('errors.coachNotWorking'),
                },
            });

            return;
        }

        TimeTap.createAppointmentNonProxyCcmOnly(sessionToken, appointmentData)
            .then(data => {
                dispatch({
                    type: 'APPOINTMENT_DETAILS',
                    payload: {
                        startDate: new Date(data?.startDateTimeUTC),
                        endDate: new Date(data?.endDateTimeUTC),
                        timezone: selectedTimezone.value,
                        type: data?.reason?.reasonDesc,
                        coach:
                            data?.staff?.internalDisplayName ??
                            data?.staff?.fullName,
                        method: data?.location?.locationName,
                    },
                });
                dispatch({
                    type: 'VIEW',
                    payload: {
                        enableNewUi: true,
                        view: 'fullview',
                        dontShowLoader: true,
                        calendarId: data?.calendarId,
                    },
                });

                // Track successful appointment scheduling
                AmpliUtils.setTrackingData(
                    TRACK_EVENT_KEYS.APPOINTMENT_SCHEDULED,
                    {
                        [TRACK_EVENT_KEYS.CalendarId]: data?.calendarId,
                        [TRACK_EVENT_KEYS.StartDate]: data?.startDate,
                        [TRACK_EVENT_KEYS.StartTime]: data?.startTime,
                        [TRACK_EVENT_KEYS.Timezone]: data?.apptTZ,
                        [TRACK_EVENT_KEYS.CoachName]:
                            data?.staff?.internalDisplayName ??
                            data?.staff?.fullName,
                    }
                );

                MixpanelUtils.track({
                    event: 'coaching.scheduling.success',
                    properties: {
                        enableNewUi: true,
                        clientStartDate: appointmentData.clientStartDate,
                        clientStartTime: appointmentData.clientStartTime,
                        staff: appointmentData.staff, // or coachData[0] directly
                    },
                });

                if (ENABLE_TRACKING_APPOINTMENT_REQUEST) {
                    MixpanelUtils.track({
                        event: 'coaching.scheduling.appointment.response',
                        properties: {
                            webSessionId,
                            calendarId: data?.calendarId,
                            startDate: data?.startDate,
                            startTime: data?.startTime,
                            timezone: data?.apptTZ,
                            type: data?.reason?.reasonDesc,
                            reasonId: data?.reason?.reasonId,
                            coach:
                                data?.staff?.internalDisplayName ??
                                data?.staff?.fullName,
                            email: data?.staff?.email,
                        },
                    });
                }
                navigate(
                    `/v2/confirmation?enableNewUi=true&view=fullview&showSession=true&calendarId=${
                        data?.calendarId
                    }&${isMobile ? 'mobile=true' : ''}`
                );
            })
            .catch(err => {
                if (
                    err.data.details.includes('is NOT available') ||
                    err.data.details.includes('ALREADY_BOOKED')
                ) {
                    setIsAlreadyBooked(true);

                    // Track already booked error
                    AmpliUtils.setTrackingData(
                        TRACK_EVENT_KEYS.ALREADY_BOOKED_ERROR,
                        {
                            [TRACK_EVENT_KEYS.ErrorDetails]: err?.data?.details,
                        }
                    );
                }

                setAPIError(err);

                MixpanelUtils.track({
                    event: 'coaching.scheduling.failure',
                    properties: {
                        'Error details': err.data,
                        payload: appointmentData,
                    },
                });
            });

        // Check if user has schedule a coaching session before.
        SharedCoaching.getLaunchpadStatus(token)
            .then(({items}) => {
                const coachStatus = items?.find(
                    item => item.name === 'COACHING'
                );

                // User has not schedule a session
                if (!coachStatus.completed) {
                    const markItem = {
                        item: {name: 'COACHING', completed: true},
                    };

                    SharedCoaching.markLaunchpadStatus(token, markItem);
                }
            })
            .catch(err => {
                // silent
            });
        MixpanelUtils.track({
            event: 'coaching.clicked.button.submit',
        });
    };

    const getSchedulerFieldsObjects = useCallback(() => {
        TimeTap.getSchedulerFieldsObjects(sessionToken)
            .then(data => {
                setCommentFieldObject(data[0]);
                setUuidFieldObject(data[5]);
                setSourceFieldObject(data[6]);
            })
            .catch(error => {
                setAPIError(error);
            });

        return;
    }, [sessionToken]);

    const languageChanged = ({target: {value}}) => {
        const locale = value;
        // Resetting these values to reset the calendar and time selection until new dates are fetched for the new serviceId

        setInitialMonth(null);
        setShowWaitModal(true);
        setAvailableDates([]);

        renderTimeSelection();

        setServiceId(
            serviceTypesIds[
                `${rankedProgram}${
                    !hasPastSession ? '_INITIAL_' : '_FOLLOWUP_'
                }${locale === 'en-US' ? 'ENGLISH' : 'SPANISH'}`
            ]
        );

        if (
            prevCoachHasNoAvailable2MonthsOut ||
            selectedCoachId === ANY_COACH
        ) {
            getAllCoachesAvailableDates();
        } else {
            getCoachAvailableDates();
        }

        // Track language change
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.LANGUAGE_CHANGED, {
            [TRACK_EVENT_KEYS.SelectedLanguage]: locale,
        });

        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.language',
            properties: {
                selectedLagugage: locale,
            },
        });

        changeLanguage(locale);
    };

    const differentCoachChange = target => {
        const selected =
            target?.target?.id === 'Any Coach' ? ANY_COACH : target?.target?.id;

        // Trigerring a re-render for the calendar so that it shows the
        // placeholder text instead of the selected day for the previous coach
        setInitialMonth(null);
        setShowWaitModal(true);
        setAvailableDates([]);
        setPreviousCoachNotAvailable2MonthsOut(false);

        renderTimeSelection();

        if (selected === ANY_COACH) {
            setIsPreviousCoachSelected(false);
            setSelectedAnyCoach(true);
            getAllCoachesAvailableDates();
            setSelectedCoachId(selected);
            setShowFirstVisitMsg(true);

            // Track any coach selection
            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.ANY_COACH_SELECTED);
        } else if (!isNil(coaches[0].key)) {
            setIsPreviousCoachSelected(true);
            setSelectedAnyCoach(false);
            setSelectedCoachId(selected);
            getCoachAvailableDates();
            setShowFirstVisitMsg(false);

            // Track specific coach selection
            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.COACH_SELECTED, {
                [TRACK_EVENT_KEYS.CoachId]: selected,
                [TRACK_EVENT_KEYS.IsPreviousCoach]: true,
            });
        }
        MixpanelUtils.track({
            event: 'coaching.scheduling.clicked.coach',
            properties: {
                selectedCoach: selected,
            },
        });
    };

    const pastSessionChanged = ({target: {value}}) => {
        if (value.toString() === CONFIRMATION[1].value.toString()) {
            setShowFirstVisitMsg(true);
            setShowPastVisitMsg(false);
        } else {
            setShowFirstVisitMsg(false);
            setShowPastVisitMsg(true);
        }

        // Track past session selection
        AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.PAST_SESSION_CHANGED, {
            [TRACK_EVENT_KEYS.HasPastSession]:
                value.toString() === CONFIRMATION[1]?.value?.toString(),
        });
    };

    useEffect(() => {
        setSessionToken(token);
    }, [token]);

    useEffect(() => {
        if (sessionToken) {
            setServiceTypeId();
        }
    }, [sessionToken, setServiceTypeId]);

    useEffect(() => {
        if (
            sessionToken &&
            serviceId &&
            clientObject &&
            !isEmpty(listOfCoachNames) &&
            !hasCalledLastSessionData.current
        ) {
            hasCalledLastSessionData.current = true;
            getLastSessionData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sessionToken,
        serviceId,
        clientObject,
        listOfCoachNames,
        // Using ref to prevent duplicate calls
    ]);

    useEffect(() => {
        if (sessionToken && serviceId) {
            getClientObject();
            getLocationObject();
            getReasonObject();
            getCoaches();
            getSchedulerFieldsObjects();
        }
    }, [
        sessionToken,
        serviceId,
        getClientObject,
        getLocationObject,
        getReasonObject,
        getCoaches,
        getSchedulerFieldsObjects,
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (sessionToken && serviceId) {
            if (selectedCoachId && !selectedAnyCoach) {
                setTimeout(() => {
                    // add a slight delay to allow the state change to execute properly
                    getCoachAvailableDates();
                }, 2000);
            }
        }
    }, [
        sessionToken,
        serviceId,
        selectedCoachId,
        selectedAnyCoach,
        getCoachAvailableDates,
    ]);

    useEffect(() => {
        setEnableNewUi(
            CommonUtils.isFeatureEnabled(
                featureFlags,
                FEATURE_FLAG_NAMES.enableNewUi,
                oneApp
            )
        );
    }, [featureFlags, oneApp]);

    useEffect(() => {
        if (token && uuid) {
            UserInfoAPI.schedulingData(token, uuid)
                .then(data => {
                    dispatch({
                        type: 'USER_DATA',
                        payload: data,
                    });
                })
                .catch(({data}) => {
                    dispatch({
                        type: 'ERROR',
                        payload: JSON.stringify(data),
                    });
                    dispatch(UserInfoAPI.incorrectToken);
                });
            UserInfoAPI.getInternalUserId(token, uuid)
                .then(data => {
                    setInternalUserId(data?.uuid);
                })
                .catch(err => {
                    setAPIError(err);
                });
        }
    }, [token, uuid, dispatch]);

    useEffect(() => {
        if (APIError) {
            setShowWaitModal(false);

            // Track error in Amplitude
            AmpliUtils.setTrackingData(TRACK_EVENT_KEYS.SCHEDULING_ERROR, {
                [TRACK_EVENT_KEYS.ErrorCode]: APIError.status,
                [TRACK_EVENT_KEYS.ErrorDetails]:
                    APIError.data?.details || 'Unknown error',
            });

            MixpanelUtils.track({
                event: 'coaching.scheduling.error',
                properties: {
                    'Error Code': APIError.status,
                },
            });
        }
    }, [APIError]);

    const defaultLanguage = selectedLang;

    useEffect(() => {
        MixpanelUtils.identify(pid);
        MixpanelUtils.track({
            event: 'coaching.scheduling.viewed',
            properties: {
                schedulingForm: 'V2',
            },
        });
    }, [pid]);

    useEffect(() => {
        contentRef?.current?.focus();
    }, []);

    return (
        <div>
            <AcuityError
                isOpen={APIError}
                toggleModal={setAPIError}
                isAlreadyBooked={isAlreadyBooked}
            />
            <Modal
                isOpen={showWaitModal}
                classNameContainer={css2.modelContainer}
            >
                <Loader
                    classNameRoot={css2.loader}
                    loadingText={t('loaderInfomation.loaderText')}
                />
            </Modal>
            <div className={css2.root}>
                <Form
                    className={css2.innerFrame}
                    onSubmit={() => {
                        scheduleAppointment();
                    }}
                >
                    <div className={css2.header}>
                        <div className={css2.logoWrapper}>
                            {!isMobile && (
                                <div className={css2.newHeader}>
                                    {t('newTitle')}
                                </div>
                            )}
                            {isNull(hasSessionInPastYear) && <Loader />}
                            {!isNull(hasSessionInPastYear) &&
                                !hasSessionInPastYear && (
                                    <div className={css2.tipWrapperClear}>
                                        <span className={css2.description}>
                                            {t(
                                                'sessionInfo.firstVisitWithCoach.description'
                                            )}
                                        </span>
                                    </div>
                                )}
                            <div>{t('required')}</div>
                        </div>
                        <RadioGroup
                            id="languageTag"
                            name="languageTag"
                            label={
                                <Label aria-hidden={isMobile}>
                                    {t('sessionInfo.newLanguage')}
                                </Label>
                            }
                            error={
                                <FormElementError>
                                    {t('form.mealTypeError')}
                                </FormElementError>
                            }
                            onChange={e => languageChanged(e)}
                        >
                            {Object.keys(languages).map(language => {
                                return (
                                    <Chip
                                        key={languages[language].key}
                                        id={languages[language].key}
                                        value={String(
                                            languages[language].value
                                        )}
                                        label={
                                            languages[
                                                language
                                            ].key.toString() === 'english'
                                                ? t(`sessionInfo.english`)
                                                : t(`sessionInfo.spanish`)
                                        }
                                        defaultChecked={
                                            languages[language].value ===
                                            defaultLanguage
                                        }
                                        role="radio"
                                    />
                                );
                            })}
                        </RadioGroup>
                        {showPastSession &&
                            !isNull(hasSessionInPastYear) &&
                            hasSessionInPastYear && (
                                <CheckableGroup
                                    id="experienceTag"
                                    name="experienceTag"
                                    label={
                                        <Label aria-hidden={isMobile}>
                                            {t('sessionInfo.newPastSession')}
                                        </Label>
                                    }
                                    error={
                                        <FormElementError>
                                            {t('form.mealTypeError')}
                                        </FormElementError>
                                    }
                                    key={CONFIRMATION?.toString()}
                                    required
                                    allowMultiSelect={false}
                                    onChange={e => pastSessionChanged(e)}
                                >
                                    {CONFIRMATION.map((confirmation, index) => {
                                        return (
                                            <Chip
                                                key={confirmation.key}
                                                id={confirmation.key}
                                                value={String(
                                                    confirmation.value
                                                )}
                                                label={
                                                    confirmation.key === 'No'
                                                        ? t(`myStrength.no`)
                                                        : t(`myStrength.yes`)
                                                }
                                                defaultChecked={
                                                    hasSessionInPastYear?.toString() ===
                                                    confirmation.value?.toString()
                                                }
                                                aria-label={`${t(
                                                    'sessionInfo.pastSession',
                                                    {
                                                        name: confirmation.key,
                                                        number: index + 1,
                                                        total: Object.keys(
                                                            CONFIRMATION
                                                        ).length,
                                                        title: t(
                                                            'sessionInfo.pastSession'
                                                        ),
                                                    }
                                                )}`}
                                                role="radio"
                                            />
                                        );
                                    })}
                                </CheckableGroup>
                            )}
                        {!prevCoachHasNoAvailable2MonthsOut &&
                            (coaches[0].key !== '' ||
                                coaches[0].value !== '') &&
                            showPastVisitMsg && (
                                <RadioGroup
                                    id="differentCoach"
                                    name="differentCoach"
                                    label={
                                        <Label aria-hidden={isMobile}>
                                            {t('sessionInfo.preferred')}
                                        </Label>
                                    }
                                    error={
                                        <FormElementError>
                                            {t('form.mealTypeError')}
                                        </FormElementError>
                                    }
                                    onChange={e => differentCoachChange(e)}
                                    role="radiogroup"
                                >
                                    {coaches.map(coach => {
                                        return (
                                            <Chip
                                                key={coach.key}
                                                id={coach.key}
                                                value={coach.key}
                                                label={`${coach.value}`}
                                                defaultChecked={
                                                    coach.key === coaches[0].key
                                                }
                                                role="radio"
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            )}
                        {hasSessionInPastYear && showFirstVisitMsg && (
                            <div className={css2.tipWrapper}>
                                <span className={css2.iconInfo}>
                                    <IconInfoDefault />
                                </span>
                                <span className={css2.description}>
                                    <span className={css2.firstTimeDesc}>
                                        {t(
                                            'sessionInfo.firstVisitWithCoach.title'
                                        )}
                                    </span>
                                    <br />
                                    {t(
                                        'sessionInfo.firstVisitWithCoach.description'
                                    )}
                                </span>
                            </div>
                        )}
                        <UserInfo isCCM isMobile={isMobile} />
                        <div className={css2.timeContainer}>
                            <DatePicker
                                placeHolder={t('Selectadate')}
                                key={initialMonth}
                                id="date"
                                name="date"
                                classNameRoot={cnb({
                                    [css2.dateTimePicker]: isMobile,
                                })}
                                classNameInput={css2.datePickerInput}
                                calendar={{
                                    disablePast: true,
                                    disableBefore: initialMonth,
                                    initialMonth,
                                    available: availableDates,
                                }}
                                disabled={!initialMonth}
                                label={
                                    <Label aria-hidden={isMobile}>
                                        {t('sessionInfo.date')}
                                    </Label>
                                }
                                onChange={date => onDaySelected(date)}
                                required
                                aria-label={t('sessionInfo.calendar')}
                            />
                        </div>
                    </div>
                    <div>
                        <Select
                            id="timeZone"
                            name="timeZone"
                            label={
                                <Label aria-hidden={isMobile}>
                                    {t('sessionInfo.timezone')}
                                </Label>
                            }
                            placeholder={t('sessionInfo.timezonePlaceholder2')}
                            defaultValue={null}
                            items={timeZones[selectedCountry].map(
                                ({value, label}) => ({
                                    value,
                                    label: t(`timeZone.${label}`),
                                })
                            )}
                            onChange={tz => timezoneChange(tz)}
                            required
                        />
                        <Select
                            key={timeKey}
                            id="time"
                            name="time"
                            disabled={isEmpty(availableTimes)}
                            label={
                                <Label aria-hidden={isMobile}>
                                    {t('sessionInfo.time')}
                                </Label>
                            }
                            items={availableTimes}
                            onChange={time => onTimeSelected(time)}
                            placeholder={t('sessionInfo.timePlaceholder')}
                            required
                        />
                    </div>
                    <TextArea
                        id="note"
                        name="note"
                        maxLength={255}
                        showCounter
                        label={<Label>{t('sessionInfo.note')}</Label>}
                        onChange={e => setNotes(e.target.value)}
                    />
                    {sessionKey !== '0' && (
                        <div
                            key={sessionKey}
                            ref={contentRef}
                            role="alert"
                            aria-live="assertive"
                            aria-atomic="true"
                            className={css2.xScreenReader}
                        >
                            {t('sessionInfo.loading')}
                        </div>
                    )}
                    <div className={css2.actions}>
                        <Button
                            key={sessionKey}
                            id="scheduleSession"
                            name="scheduleSession"
                            type="submit"
                            variant="primary"
                            className={css2.confirmButton}
                            disabled={
                                disableScheduleButton ||
                                isNull(coachId) ||
                                isNull(selectedCoachId)
                            }
                            loading={sessionKey !== '0'}
                        >
                            {t('sessionInfo.submit')}{' '}
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default SchedulingFormV2;
